import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Instagram, Youtube, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fileUrl = (p) => p?.startsWith("http") ? p : (p ? `${BACKEND_URL}/api/files/${p}` : "");

const BUDGETS = ["< ₹5L", "₹5L – ₹15L", "₹15L – ₹40L", "₹40L – ₹1Cr", "₹1Cr+"];
const BUDGET_TO_INR = { "< ₹5L": 250000, "₹5L – ₹15L": 1000000, "₹15L – ₹40L": 2500000, "₹40L – ₹1Cr": 6000000, "₹1Cr+": 10000000 };
const CATEGORIES = [
  { key: "client", label: "Client project" },
  { key: "sponsorship", label: "Sponsorship" },
  { key: "collaboration", label: "Collaboration" },
  { key: "branding", label: "Branding deal" },
];

export default function PublicCreator() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", budget_range: BUDGETS[1], category: "client" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/creator/${slug}`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.status === 404 ? "Creator not found" : "Could not load profile");
      }
      const p = new URLSearchParams(window.location.search);
      const dep = p.get("deposit");
      const sid = p.get("session_id");
      if (dep === "success" && sid) {
        try {
          await axios.get(`${API}/payments/status/${sid}`);
          toast.success("Deposit received — booking confirmed!");
        } catch { /* ignore */ }
        window.history.replaceState(null, "", `/u/${slug}`);
      } else if (dep === "cancel") {
        toast("Deposit cancelled — booking not confirmed.");
        window.history.replaceState(null, "", `/u/${slug}`);
      }
    })();
  }, [slug]);

  const submitBooking = async () => {
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Name, valid email and message required");
      return;
    }
    setSending(true);
    try {
      const deposit_amount = (data.deposit_pct || 0) > 0
        ? Math.round((BUDGET_TO_INR[form.budget_range] || 0) * (data.deposit_pct / 100) * 100) / 100
        : 0;
      const r = await axios.post(`${API}/public/creator/${slug}/book`, {
        ...form,
        origin_url: window.location.origin,
        deposit_amount,
      });
      if (r.data.checkout_url) {
        toast.success("Redirecting to deposit checkout…");
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Booking sent — they'll be in touch.");
      setOpen(false);
      setForm({ name: "", email: "", company: "", message: "", budget_range: BUDGETS[1], category: "client" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  if (err) return <div className="min-h-screen bg-black grain text-white flex items-center justify-center"><div className="mono text-coral">{err.toUpperCase()}</div></div>;
  if (!data) return <div className="min-h-screen bg-black grain text-white flex items-center justify-center"><div className="mono text-kinetic animate-pulse">LOADING…</div></div>;

  const cover = fileUrl(data.cover_image) || "https://images.pexels.com/photos/6485524/pexels-photo-6485524.jpeg";
  const avatar = fileUrl(data.picture);
  const s = data.stats;

  return (
    <div className="min-h-screen bg-black grain text-white" data-testid="public-creator">
      {/* Cover */}
      <div className="relative h-[42vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center scale-110" style={{ backgroundImage: `url(${cover})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        <div className="absolute top-0 left-0 right-0 px-8 py-6 flex justify-between items-center">
          <a href="/" className="flex items-center gap-2"><div className="w-7 h-7 bg-kinetic"/><span className="font-extrabold tracking-tight">INDIEWOOD</span></a>
          <span className="mono text-white/40">AN · INFLUENCER · FACTORY</span>
        </div>
      </div>

      <div className="-mt-28 relative px-6 max-w-5xl mx-auto">
        <div className="flex items-end gap-6 flex-wrap">
          <Avatar className="w-32 h-32 rounded-sm ring-4 ring-black">
            <AvatarImage src={avatar} className="object-cover" />
            <AvatarFallback className="bg-white/10 rounded-sm text-3xl">{data.name?.[0] || "C"}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="mono text-kinetic">@{data.slug}</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">{data.name}</h1>
            {data.headline && <p className="mt-3 text-white/80 text-lg max-w-2xl">{data.headline}</p>}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="book-me-btn" disabled={!data.accepting_bookings} className="kinetic-btn rounded-sm h-12 px-8 text-base disabled:opacity-50">
                {data.accepting_bookings ? "Book me →" : "Not taking bookings"}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-sans text-2xl">Book {data.name?.split(" ")[0]}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="mono text-white/60">Type</Label>
                  <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="book-category" className="bg-black border-white/15 rounded-sm mono"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
                      {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key} className="mono">{c.label.toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {[["name", "Your name"], ["email", "Email"], ["company", "Company (optional)"]].map(([k, l]) => (
                  <div key={k}>
                    <Label className="mono text-white/60">{l}</Label>
                    <Input data-testid={`book-${k}`} value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} className="bg-black border-white/15 rounded-sm" />
                  </div>
                ))}
                <div>
                  <Label className="mono text-white/60">Budget</Label>
                  <Select value={form.budget_range} onValueChange={(v) => setForm(f => ({ ...f, budget_range: v }))}>
                    <SelectTrigger data-testid="book-budget" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
                      {BUDGETS.map(b => <SelectItem key={b} value={b} className="mono">{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mono text-white/60">Project brief</Label>
                  <Textarea data-testid="book-message" value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} className="bg-black border-white/15 rounded-sm min-h-28" placeholder="Tell them about the project, timeline, vibe…" />
                </div>
                {(data.deposit_pct || 0) > 0 && (
                  <div className="border border-kinetic/30 bg-kinetic/5 px-3 py-2 mono text-kinetic" data-testid="deposit-notice">
                    DEPOSIT · {data.deposit_pct}% (~₹{Math.round((BUDGET_TO_INR[form.budget_range] || 0) * (data.deposit_pct / 100)).toLocaleString('en-IN')}) PAYABLE TO CONFIRM
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="book-submit" onClick={submitBooking} disabled={sending} className="kinetic-btn rounded-sm">{sending ? "SENDING…" : "Send booking →"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-10">
          {[
            ["PROJECTS", s.projects],
            ["DELIVERED", s.delivered],
            ["QC PASS", `${s.qc_pass_pct}%`],
            ["GMV", `₹${(s.revenue_delivered || 0).toLocaleString('en-IN')}`],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#0E0E0E] p-5">
              <div className="mono text-white/40">{k}</div>
              <div className="font-sans text-3xl font-extrabold mt-2">{v}</div>
            </div>
          ))}
        </div>

        {/* Bio + socials */}
        {(data.bio || data.instagram || data.youtube || data.tiktok) && (
          <div className="mt-10 border border-white/10 bg-[#0C0C0C] p-6">
            {data.bio && <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{data.bio}</p>}
            <div className="flex gap-3 mt-5 flex-wrap">
              {data.instagram && <a target="_blank" rel="noreferrer" href={`https://instagram.com/${data.instagram.replace(/^@/, '')}`} className="mono px-3 py-2 border border-white/15 hover:border-kinetic transition flex items-center gap-2"><Instagram className="w-3.5 h-3.5"/> {data.instagram}</a>}
              {data.youtube && <a target="_blank" rel="noreferrer" href={data.youtube} className="mono px-3 py-2 border border-white/15 hover:border-kinetic transition flex items-center gap-2"><Youtube className="w-3.5 h-3.5"/> YouTube</a>}
              {data.tiktok && <a target="_blank" rel="noreferrer" href={`https://tiktok.com/@${data.tiktok.replace(/^@/, '')}`} className="mono px-3 py-2 border border-white/15 hover:border-kinetic transition flex items-center gap-2">TT · {data.tiktok}</a>}
            </div>
          </div>
        )}

        {/* Availability */}
        {data.availability?.length > 0 && (
          <div className="mt-10" data-testid="public-availability">
            <div className="mono text-white/60 mb-4">AVAILABILITY</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {data.availability.map((a, i) => (
                <div key={i} className="bg-[#0E0E0E] p-4">
                  <div className="mono text-kinetic">{a.label || "OPEN WINDOW"}</div>
                  <div className="font-sans font-bold mt-2">{a.start || "—"} → {a.end || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Showcase */}
        {data.showcase?.length > 0 && (
          <div className="mt-10" data-testid="public-showcase">
            <div className="mono text-white/60 mb-4">SHOWCASE · PREVIOUS WORK</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.showcase.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" className="border border-white/10 bg-[#0C0C0C] hover:border-kinetic transition p-4 block">
                  <div className="mono text-kinetic">{(s.kind || "LINK").toUpperCase()}</div>
                  <div className="font-sans font-bold text-lg mt-1">{s.title || s.url}</div>
                  <div className="mono text-white/40 mt-1 truncate">{s.url}</div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Rate cards */}
        {data.rate_cards?.length > 0 && (
          <div className="mt-10" data-testid="public-rate-cards">
            <div className="mono text-white/60 mb-4">RATE CARDS</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {data.rate_cards.map((c, i) => (
                <div key={i} className="bg-[#0E0E0E] p-5">
                  <div className="mono text-white/40 flex items-center justify-between">
                    <span>{c.service}</span>
                    {c.model && <span className="text-kinetic">· {c.model.toUpperCase()}</span>}
                  </div>
                  <div className="font-sans text-2xl font-extrabold mt-2 text-kinetic">{c.price_range}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured */}
        <div className="mt-10">
          <div className="mono text-white/60 mb-4 flex items-center gap-2"><Sparkles className="w-3 h-3 text-kinetic"/> RECENT WORK</div>
          {data.featured.length === 0 && <div className="mono text-white/30 border border-dashed border-white/15 p-12 text-center">NO PUBLIC PROJECTS YET</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.featured.map(p => (
              <div key={p.id} className="border border-white/10 bg-[#0C0C0C] hover:border-kinetic transition p-5">
                <div className="mono text-white/40">{p.client_name}</div>
                <div className="font-sans font-bold text-lg mt-1">{p.title}</div>
                {p.description && <p className="text-white/60 mt-2 line-clamp-3">{p.description}</p>}
                <div className="mono text-kinetic mt-3 uppercase">{p.stage}</div>
              </div>
            ))}
          </div>
        </div>

        <footer className="mono text-white/30 py-12 text-center mt-16 border-t border-white/10">
          INDIEWOOD · THE INDIE MEGAPLEX · SAAS
        </footer>
      </div>
    </div>
  );
}
